import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useStoreContext } from 'features/store';
import isEqual from 'lodash-es/isEqual';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { ServiceMode } from 'generated/graphql-gateway';
import { IPriceOrderParams, priceOrderErrorStatuses, usePriceOrder } from 'hooks/price-order';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useStableCallback } from 'hooks/use-stable-callback';
import { useAuthContext } from 'state/auth';
import { mapEntryTypeToOfferType } from 'state/cart/helpers';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useTransformedAppliedOffers } from 'state/loyalty/hooks/use-transformed-applied-offers';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useCartTip } from 'state/order/hooks/use-cart-tip';
import { useUnavailableCartEntries } from 'state/order/hooks/use-unavailable-cart-entries';
import { getServiceModeFromLS, useServiceModeLive } from 'state/service-mode';
import { createCartEntry } from 'utils/cart';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { isPhoneNumberValidSimple } from 'utils/form';
import { isDelivery } from 'utils/service-mode';

import { DISCOUNT_OFFER_CART_ID } from './constants';
import { getPriceOrderCustomerName } from './customer-name';
import { useRepriceOnServiceModeMismatch } from './hooks/use-reprice-on-service-mode-mismatch';
import { useSanitizedRewards } from './hooks/use-sanitized-rewards';
import type {
  ICartContext,
  ICartContextArgs,
  ITryPricingParams,
  IVehicleDescription,
  IVehicleDescriptionChange,
} from './types';

export const emptyCartContext = {} as ICartContext;
const CartContext = createContext<ICartContext>(emptyCartContext);

export const CartProvider: React.FC<React.PropsWithChildren<ICartContextArgs>> = ({ children }) => {
  const serviceMode = getServiceModeFromLS();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const [a11yMessage, setA11yMessage] = useState<string>('');

  const [cardSuccessfullyAdded, setCardSuccessfullyAdded] = useState(false);

  const [shouldSkipPriceOrder, setShouldSkipPriceOrder] = useState(false);

  const [isTipLocked, setIsTipLocked] = useState(false);

  const {
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    isStoreChangeHydrationInProgress,
  } = useOrderContext();
  const {
    cartEntries: globalCartEntries,
    calculateCartTotalWithDiscount,
    removeFromCart,
    repriceCartEntries,
  } = useCart();

  const {
    tipAmount,
    setTipAmount,
    tipSelection,
    setTipSelection,
    updateTipAmount,
    shouldShowTipPercentage,
  } = useCartTip();

  const [vehicleDescription, _setVehicleDescription] = useState<IVehicleDescription>({
    value: syncStorage.getString(SyncStorageKey.VEHICLE_DESCRIPTION) ?? '',
    hasError: false,
  });

  const setVehicleDescription = useCallback(
    ({ hasError = false, value }: IVehicleDescriptionChange) => {
      _setVehicleDescription((prevVehicleDescription: IVehicleDescription) => {
        const newVehicleDescription = {
          hasError,
          value,
        };

        if (value) {
          syncStorage.setString(SyncStorageKey.VEHICLE_DESCRIPTION, value);
        } else {
          syncStorage.remove(SyncStorageKey.VEHICLE_DESCRIPTION);
        }

        return isEqual(prevVehicleDescription, newVehicleDescription)
          ? prevVehicleDescription
          : newVehicleDescription;
      });
    },
    [_setVehicleDescription]
  );

  const saveVehicleDescription = useCallback((description: string) => {
    syncStorage.setString(SyncStorageKey.VEHICLE_DESCRIPTION, description);
  }, []);

  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);

  const { user, refreshCurrentUser } = useAuthContext();
  const { isStoreOpenAndAvailable, store } = useStoreContext();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const loyaltyAppliedDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );

  const { locale: customerLocale } = useLocale();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // TODO: having a deps array will trigger this cleanup function every time that the deps changes
    // Check if this is intentional or this should only be called on unmount
    return () => {
      if (deliveryAddress?.shouldSave) {
        refreshCurrentUser();
      }
    };
  }, [deliveryAddress, refreshCurrentUser]);

  const appliedOffersMap = useMemo(
    () =>
      appliedOffers.reduce((acc, offer) => {
        acc[offer.cartId || ''] = offer;
        return acc;
      }, {} as Record<string, LoyaltyAppliedOffer>),
    [appliedOffers]
  );

  const cartEntries = useMemo(() => {
    const globalDiscountEntry = globalCartEntries.find(
      (entry: ILegacyCartEntry) => entry.cartId === DISCOUNT_OFFER_CART_ID
    );
    const normalizedGlobalCartEntries = globalCartEntries.filter(
      (entry: ILegacyCartEntry) => entry.cartId !== DISCOUNT_OFFER_CART_ID
    );

    // Create the cart entry from the selected discount offer.
    // Legacy carts always synthesize the row; unified carts prefer the persisted row
    // but repair or synthesize it when a store switch leaves a stale discount entry behind.
    // @ts-expect-error TS(2322) FIXME: ICartEnty does not support empty vendorConfigs
    const syntheticDiscountEntry: ILegacyCartEntry | null =
      normalizedGlobalCartEntries.length &&
      loyaltyAppliedDiscountOffer &&
      isDiscountOffer(loyaltyAppliedDiscountOffer)
        ? {
            ...createCartEntry({ item: loyaltyAppliedDiscountOffer }),
            cartId: DISCOUNT_OFFER_CART_ID,
            vendorConfigs: undefined,
            offerVendorConfigs: loyaltyAppliedDiscountOffer.vendorConfigs,
          }
        : null;

    const normalizedUnifiedDiscountEntry =
      isUnifiedMenuEnabled && globalDiscountEntry && syntheticDiscountEntry
        ? {
            ...globalDiscountEntry,
            _id: globalDiscountEntry._id || syntheticDiscountEntry._id,
            sanityId:
              globalDiscountEntry.sanityId ||
              globalDiscountEntry._id ||
              syntheticDiscountEntry.sanityId,
            name: globalDiscountEntry.name || syntheticDiscountEntry.name,
            offerVendorConfigs:
              globalDiscountEntry.offerVendorConfigs || syntheticDiscountEntry.offerVendorConfigs,
            vendorConfigs:
              globalDiscountEntry.vendorConfigs ?? syntheticDiscountEntry.vendorConfigs,
          }
        : globalDiscountEntry;

    let offerDiscountCartEntry: ILegacyCartEntry[] = [];
    if (isUnifiedMenuEnabled) {
      const unifiedDiscountEntry = normalizedUnifiedDiscountEntry || syntheticDiscountEntry;
      if (unifiedDiscountEntry) {
        offerDiscountCartEntry = [unifiedDiscountEntry];
      }
    } else if (syntheticDiscountEntry) {
      offerDiscountCartEntry = [syntheticDiscountEntry];
    }

    // Updating entry type if its part of an applied offer
    const updatedCartEntries = normalizedGlobalCartEntries.map((entry: ILegacyCartEntry) =>
      appliedOffersMap[entry.cartId]
        ? { ...entry, type: mapEntryTypeToOfferType(entry.type) }
        : entry
    );

    // Cart entries with offers
    return repriceCartEntries(updatedCartEntries.concat(offerDiscountCartEntry));
  }, [
    appliedOffersMap,
    globalCartEntries,
    isUnifiedMenuEnabled,
    loyaltyAppliedDiscountOffer,
    repriceCartEntries,
  ]);

  const { requestedAmountCents, isCartTotalNegative } = useMemo(() => {
    const { cartTotal, isCartTotalNegative: negative } = calculateCartTotalWithDiscount(
      cartEntries
    );
    return {
      requestedAmountCents: Math.max(0, cartTotal),
      isCartTotalNegative: negative,
    };
  }, [cartEntries, calculateCartTotalWithDiscount]);

  const rewardsApplied = useSanitizedRewards({ cartEntries });

  const {
    rbiOrderId,
    priceOrder,
    loading: isOrderPriceLoading,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: { error: priceMutationError },
    serverOrder,
    isOrderPricingRetryInProgress,
    didOrderPricingRetryFail,
    isPricingFailureRecoverable,
    setIsRepricing,
  } = usePriceOrder({
    cartEntries,
    updateTipAmount,
  });

  // TODO: BKPE-1956 - unavailableCartEntries
  const { unavailableCartEntries, setUnavailableCartEntries } = useUnavailableCartEntries({
    cartEntries,
    serverOrder,
    isStoreChangeHydrationInProgress,
  });

  const announceA11yCartMessage = useCallback((message: string) => {
    setA11yMessage('');

    setTimeout(() => {
      setA11yMessage(message);
    }, 100);
  }, []);

  const onConfirmRemoveItemDialog = useCallback(
    (entry: ILegacyCartEntry | null | undefined) => {
      if (!entry) {
        return;
      }
      announceA11yCartMessage(`${t('removeFromCartSuccess', { itemName: entry.name })}`);
      return removeFromCart(entry);
    },
    [announceA11yCartMessage, removeFromCart]
  );

  const [RemoveItemDialog, openRemoveItemDialog, itemToRm] = useDialogModal<ILegacyCartEntry>({
    onConfirm: onConfirmRemoveItemDialog,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Remove item from cart',
  });

  // for confirming item removal
  const confirmRemoveItemFromCart = useCallback(
    (cartId: string) => {
      const entryToRemove = globalCartEntries.find(
        (item: ILegacyCartEntry) => cartId === item.cartId
      );
      if (entryToRemove) {
        openRemoveItemDialog(entryToRemove);
      }
    },
    [globalCartEntries, openRemoveItemDialog]
  );

  const getCustomerNameForServiceMode = useCallback(
    (activeServiceMode: ServiceMode | null | undefined) =>
      getPriceOrderCustomerName({
        serviceMode: activeServiceMode,
        userFullName: user?.details?.name,
        vehicleDescription: vehicleDescription.value,
        isFastLaneEnabled,
      }),
    [isFastLaneEnabled, user?.details?.name, vehicleDescription.value]
  );

  const customerName = getCustomerNameForServiceMode(serviceMode);

  const { allPricingDependenciesAreSettled } = useMenuContext();

  const validAppliedOffers = useTransformedAppliedOffers();

  const memoizedPriceOrderStoreParam = useMemo(() => {
    const priceOrderStoreParam: IPriceOrderParams['store'] = {
      number: store.number,
      posRestaurantId: store.posRestaurantId,
      pos: {
        vendor: store.pos?.vendor,
      },
      vatNumber: store.vatNumber,
      physicalAddress: {
        address1: store.physicalAddress?.address1,
        address2: store.physicalAddress?.address2,
        city: store.physicalAddress?.city,
        country: store.physicalAddress?.country,
        postalCode: store.physicalAddress?.postalCode,
        stateProvince: store.physicalAddress?.stateProvince,
      },
      phoneNumber: store.phoneNumber,
    };

    return priceOrderStoreParam;
  }, [
    store.number,
    store.posRestaurantId,
    store.pos?.vendor,
    store.vatNumber,
    store.phoneNumber,
    store.physicalAddress?.address1,
    store.physicalAddress?.address2,
    store.physicalAddress?.city,
    store.physicalAddress?.country,
    store.physicalAddress?.postalCode,
    store.physicalAddress?.stateProvince,
  ]);

  const priceOrderParams = useMemo(
    () => ({
      rewardsApplied,
      customerLocale,
      customerName,
      deliveryAddress: deliveryAddress
        ? {
            ...deliveryAddress,
            phoneNumber: orderPhoneNumber,
          }
        : null,
      orderPhoneNumber,
      deliveryInstructions,
      appliedOffers: validAppliedOffers,
      requestedAmountCents,
      serviceMode,
      store: memoizedPriceOrderStoreParam,
      isStoreOpenAndAvailable,
    }),
    [
      customerLocale,
      customerName,
      deliveryAddress,
      deliveryInstructions,
      isStoreOpenAndAvailable,
      orderPhoneNumber,
      requestedAmountCents,
      rewardsApplied,
      serviceMode,
      memoizedPriceOrderStoreParam,
      validAppliedOffers,
    ]
  );

  const _tryPricing = useCallback(
    (params?: ITryPricingParams) => {
      const currentSM = getServiceModeFromLS() ?? serviceMode;
      const freshShouldAvoid = isDelivery(currentSM) && !isPhoneNumberValidSimple(orderPhoneNumber);
      const freshEnabled =
        !freshShouldAvoid &&
        isStoreOpenAndAvailable &&
        user &&
        !isCartTotalNegative &&
        currentSM &&
        cartEntries?.length > 0 &&
        allPricingDependenciesAreSettled;

      if (!freshEnabled) {
        setShouldSkipPriceOrder(true);
        return;
      }

      syncStorage.remove(SyncStorageKey.ORDER_ID);

      if (params?.isRepriceAttempt) {
        setIsRepricing();
      }

      const freshCustomerName = getCustomerNameForServiceMode(currentSM as ServiceMode);

      priceOrder({
        ...priceOrderParams,
        ...params?.overrideOrderParams,
        customerPhoneNumber: user.details.phoneNumber,
        serviceMode: currentSM as ServiceMode,
        customerName: params?.overrideOrderParams?.customerName ?? freshCustomerName,
      });

      dispatch(actions.loyalty.setIsPricingRewardApplication(false));
    },
    [
      serviceMode,
      orderPhoneNumber,
      isStoreOpenAndAvailable,
      user,
      isCartTotalNegative,
      cartEntries?.length,
      allPricingDependenciesAreSettled,
      priceOrder,
      priceOrderParams,
      dispatch,
      setIsRepricing,
      getCustomerNameForServiceMode,
    ]
  );
  const tryPricing = useStableCallback(_tryPricing);

  const removeItemMessage = (item: ILegacyCartEntry | null) => {
    return item
      ? t('removeItemFromCart', {
          item: item.name,
          offerText: '',
        })
      : '';
  };

  const pricedAndAvailable = useMemo(() => {
    return (
      priceOrderSuccess &&
      !unavailableCartEntries.length &&
      Boolean(serverOrder?.status && !priceOrderErrorStatuses.includes(serverOrder.status))
    );
  }, [priceOrderSuccess, serverOrder?.status, unavailableCartEntries.length]);

  const selectedDonationCartEntry = useMemo(
    () => cartEntries.find((cartEntry: ILegacyCartEntry) => cartEntry.isDonation),
    [cartEntries]
  );

  const value = {
    rbiOrderId,
    confirmRemoveItemFromCart,
    serverOrder,
    tryPricing,
    isOrderPriceLoading,
    cardSuccessfullyAdded,
    setCardSuccessfullyAdded,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceMutationError,
    pricedAndAvailable,
    unavailableCartEntries,
    isPricingFailureRecoverable,
    setUnavailableCartEntries,
    shouldSkipPriceOrder,
    setShouldSkipPriceOrder,
    selectedDonationCartEntry,
    requestedAmountCents,
    tipAmount,
    setTipAmount,
    isTipLocked,
    setIsTipLocked,
    vehicleDescription,
    setVehicleDescription,
    saveVehicleDescription,
    tipSelection,
    setTipSelection,
    updateTipAmount,
    shouldShowTipPercentage,
    a11yMessage,
    announceA11yCartMessage,
    isOrderPricingRetryInProgress,
    didOrderPricingRetryFail,
  };

  return (
    <CartContext.Provider value={value}>
      {children}

      <RemoveItemDialog body={removeItemMessage(itemToRm)} heading={t('removeItem')} />
      <ServiceModeRepriceHandler />
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);

const ServiceModeRepriceHandler = () => {
  const serviceMode = useServiceModeLive();
  const { tryPricing, isOrderPriceLoading, serverOrder } = useCartContext();

  useRepriceOnServiceModeMismatch({
    serviceMode: serviceMode as ServiceMode,
    serverOrderServiceMode: serverOrder?.cart.serviceMode,
    tryPricing,
    isOrderPriceLoading,
  });

  return null;
};
